// TimezoneConversion.jsx

import { DateTime } from 'luxon';

/**
 * Convert a date from UTC to the user's timezone.
 *
 * @param {string} date - The date string in ISO format (expected to be in UTC).
 * @param {string} loggedInUserTimezone - The target timezone to convert the date to.
 * @returns {DateTime|null} - Luxon DateTime object representing the date in the target timezone.
 */
export const ConvertToMyTimeZone = (date, loggedInUserTimezone) => {
    if (date && loggedInUserTimezone) {
        return DateTime.fromISO(date, { zone: 'utc' }).setZone(loggedInUserTimezone);
    }
    return null;
};

/**
 * Convert a date from one timezone to another.
 *
 * @param {string} date - The date string in ISO format.
 * @param {string} fromTimezone - The original timezone of the date.
 * @param {string} toTimezone - The target timezone to convert the date to.
 * @returns {DateTime|null} - Luxon DateTime object representing the date in the target timezone.
 */
export const convertBetweenTimezones = (date, fromTimezone, toTimezone) => {
    if (date && fromTimezone && toTimezone) {
        return DateTime.fromISO(date, { zone: fromTimezone }).setZone(toTimezone);
    }
    return null;
};


/**
 * Convert a JavaScript Date object to an ISO date string in UTC.
 *
 * @param {Date} date - The JavaScript Date object.
 * @param {string} userTimezone - The timezone in which the date is initially set.
 * @returns {string} - ISO date string in UTC.
 */
export const convertJSDateToUTCISO = (date, userTimezone) => {
    const localDateTime = DateTime.fromJSDate(date, { zone: userTimezone });
    return localDateTime.toUTC().toISODate(); // Returns an ISO string in UTC
};

/**
 * Convert a date from a specified timezone to UTC.
 *
 * @param {string} date - The date string in ISO format.
 * @param {string} userTimezone - The timezone in which the date is initially set.
 * @returns {Date} - JavaScript Date object in UTC.
 */
export const convertToUTC = (date, userTimezone) => {
    const localDateTime = DateTime.fromISO(date, { zone: userTimezone });
    const utcDateTime = localDateTime.toUTC();
    return utcDateTime.toJSDate();
};

/**
 * Convert a date from a specified timezone to UTC but returns ISO
 *
 * @param {string} date - The date string in ISO format.
 * @param {string} userTimezone - The timezone in which the date is initially set.
 * @returns {string} - ISO string in UTC.
 */
export const convertToUTCISO = (date, userTimezone) => {
    const localDateTime = DateTime.fromISO(date, { zone: userTimezone });
    return localDateTime.toUTC().toISO(); // Return as ISO string in UTC
};

/**
 * Format a date string in a specific timezone to a readable format.
 *
 * @param {Date} date - The JavaScript Date object.
 * @param {string} local - The language which the date should be formatted as.
 * @returns {string} - The formatted date string.
 */
export const formatDateToString = (date, local = 'en') => {
    return date.setLocale(local).toFormat("dd MMM yyyy HH:mm:ss");
};

/**
 * Format a date string in a specific timezone to a readable format without seconds.
 * 
 * @param {string} dateString - The date string in ISO format.
 * @param {string} userTimezone - The timezone in which to display the formatted date.
 * @returns {string} - The formatted date string.
 */
export const formatDateToStringHHMM = (date) => {
    return date.toFormat("dd MMM yyyy HH:mm");
};
